interface CompletenessBarProps {
  percentage: number;
}

const CompletenessBar = ({ percentage = 1 }: CompletenessBarProps) => {
  const value = Math.min(Math.max(Math.round(percentage * 100), 0), 100);

  // Color interpolation functions
  const hexToRgb = (hex: string): number[] => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };

  const rgbToHex = (r: number, g: number, b: number): string => {
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = Math.round(x).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  };

  const getInterpolatedColor = (percentage: number): string => {
    const startColor = '#F88271';
    const endColor = '#2CA04C';
    const middleColor = '#7B7BE9';

    const startRgb = hexToRgb(startColor);
    const middleRgb = hexToRgb(middleColor);
    const endRgb = hexToRgb(endColor);

    const p = percentage / 100;

    // Use middle color for the middle range
    if (p < 0.5) {
      // Interpolate between start and middle
      const t = p * 2; // Scale to 0-1 range for the first half
      const r = startRgb[0] + (middleRgb[0] - startRgb[0]) * t;
      const g = startRgb[1] + (middleRgb[1] - startRgb[1]) * t;
      const b = startRgb[2] + (middleRgb[2] - startRgb[2]) * t;
      return rgbToHex(r, g, b);
    } else {
      // Interpolate between middle and end
      const t = (p - 0.5) * 2; // Scale to 0-1 range for the second half
      const r = middleRgb[0] + (endRgb[0] - middleRgb[0]) * t;
      const g = middleRgb[1] + (endRgb[1] - middleRgb[1]) * t;
      const b = middleRgb[2] + (endRgb[2] - middleRgb[2]) * t;
      return rgbToHex(r, g, b);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300 ease-in-out"
          style={{
            width: `${value}%`,
            backgroundColor: getInterpolatedColor(value),
          }}
        />
      </div>
      <span className="text-sm text-gray-200 min-w-[40px]">{value}%</span>
    </div>
  );
};

export default CompletenessBar;
